import { Button } from '@design/components/button/Button';
import {
  DatePicker,
  DatePickerLocales,
} from '@design/components/date-picker/DatePicker';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  isSameDay,
  startOfToday,
} from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardDay } from '../card-day/CardDay';
import { formatEventDate, TabsType } from '../utils';
import styles from './EventOverview.module.scss';

interface CountData {
  counts: Record<string, number>;
  isLoading: boolean;
}
export interface EventOverviewProps {
  activeDate: Date;
  endWeekDay: Date;
  startWeekDay: Date;
  isNextWeekDisable: boolean;
  onDateChange: (date: Date) => void;
  data: {
    [TabsType.Economics]: CountData;
    [TabsType.Earnings]: CountData;
    [TabsType.Company]: CountData;
  };
}

export const EventOverview = ({
  activeDate,
  endWeekDay,
  startWeekDay,
  isNextWeekDisable,
  onDateChange,
  data,
}: EventOverviewProps) => {
  const { t } = useTranslation(['calendar', 'datePicker']);

  const datePickerLocales: DatePickerLocales = {
    selectYear: t('datePicker:select_year'),
    selectMonth: t('datePicker:select_month'),
    selectDecade: t('datePicker:select_decade'),
    previousMonth: t('datePicker:previous_month'),
    previousYear: t('datePicker:previous_year'),
    previousDecade: t('datePicker:previous_decade'),
    nextMonth: t('datePicker:next_month'),
    nextYear: t('datePicker:next_year'),
    nextDecade: t('datePicker:next_decade'),
  };
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const { economics, earnings, company } = data;
  const weekDates = eachDayOfInterval({
    start: startWeekDay,
    end: endWeekDay,
  });
  const dateLabel = `${formatDateToLocale(
    languageSetting,
    startWeekDay,
    Formats['MMMM d']
  )} - ${formatDateToLocale(
    languageSetting,
    endWeekDay,
    Formats['MMMM d, y']
  )}`;

  return (
    <div>
      <div className={styles.dateWrapper}>
        <DropdownMenu
          open={isDatePickerOpen}
          onOpenChange={() => setIsDatePickerOpen(prev => !prev)}
          triggerNode={
            <Button
              className={styles.calendarButton}
              variant="tertiary"
              size="small"
              iconName="Calendar"
              aria-label={t('calendar:openCalendar')}
              data-testid="datepicker-trigger-button"
            />
          }
          contentProps={{
            asChild: true,
          }}
        >
          <div className={styles.menuWrapper}>
            <DatePicker
              value={activeDate}
              maxDate={addMonths(startOfToday(), 3)}
              onDateChange={newDate => {
                onDateChange(newDate as Date);
                setIsDatePickerOpen(false);
              }}
              locales={datePickerLocales}
              data-testid="events-calendar"
            />
          </div>
        </DropdownMenu>

        <Button
          variant="empty"
          size="small"
          iconName="ArrowLeft"
          aria-label={t('calendar:previousWeek')}
          data-testid="arrow-previous-week"
          onClick={() => onDateChange(addDays(activeDate, -7))}
        />

        <span className={styles.dateLabel} data-testid="overview-date-label">
          {dateLabel}
        </span>
        <Button
          variant="empty"
          size="small"
          iconName="ArrowRight"
          aria-label={t('calendar:nextWeek')}
          data-testid="arrow-next-week"
          disabled={isNextWeekDisable}
          onClick={() => onDateChange(addDays(activeDate, 7))}
        />
      </div>
      <div className={styles.eventCardsWrapper}>
        {weekDates.map(d => {
          const dateOnly = formatEventDate(d);

          const rows = [
            {
              label: t('calendar:earnings'),
              count: earnings.counts[dateOnly],
              bgColor: 'var(--surface-decorative-indigo-default)',
              isLoading: earnings.isLoading,
            },
            {
              label: t('calendar:economic'),
              count: economics.counts[dateOnly],
              bgColor: 'var(--surface-decorative-purple-default)',
              isLoading: economics.isLoading,
            },
            {
              label: t('calendar:company'),
              count: company.counts[dateOnly],
              bgColor: 'var(--surface-decorative-cyan-default)',
              isLoading: company.isLoading,
            },
          ];

          return (
            <CardDay
              key={d.valueOf()}
              date={d}
              isActive={isSameDay(d, activeDate)}
              handleCardClick={() => onDateChange(d)}
              rows={rows}
            />
          );
        })}
      </div>
    </div>
  );
};
